import { Button, Input, Select, SegmentedTabs } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_PLAN_REPORT_FILTERS,
  type PlanReportFilters,
  type PlanReportSummary,
  type PlanReportView,
} from "./plansModel";

interface Props {
  filters: PlanReportFilters;
  onChange: (next: PlanReportFilters) => void;
  summary: PlanReportSummary;
}

export function PlansFiltersPanel({ filters, onChange, summary }: Props) {
  const { t } = useTranslation("daycareHotel");
  const set = <K extends keyof PlanReportFilters>(key: K, value: PlanReportFilters[K]) => onChange({ ...filters, [key]: value });
  const hasRefinements = filters.search !== "" || filters.type !== "all" || filters.payment !== "all" || filters.consumption !== "all" || filters.status !== "all";
  const all = t("plans.report.optionAll");

  return (
    <section className="grid gap-4 rounded-2xl border bg-card p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-sm font-semibold">{t("plans.report.filtersTitle")}</p>
          <p className="text-xs text-muted-foreground">{t("plans.report.filtersDescription")}</p>
        </div>
        <SegmentedTabs
          value={filters.view}
          onValueChange={(value) => set("view", value as PlanReportView)}
          items={[
            { value: "attention", label: t("plans.report.viewAttention", { count: summary.attention }) },
            { value: "all", label: t("plans.report.viewAll", { count: summary.total }) },
          ]}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <label className="grid gap-1.5 text-sm font-medium text-muted-foreground sm:col-span-2 xl:col-span-1">
          {t("plans.report.searchLabel")}
          <Input aria-label={t("plans.report.searchLabel")} onChange={(event) => set("search", event.target.value)} placeholder={t("plans.report.searchPlaceholder")} type="search" value={filters.search} />
        </label>
        <Select label={t("plans.report.typeLabel")} onValueChange={(value) => set("type", value as PlanReportFilters["type"])} value={filters.type} options={[{ value: "all", label: all }, { value: "daycare", label: t("typeDaycare") }, { value: "hotel", label: t("typeHotel") }, { value: "daycare_overnight", label: t("typeDaycareOvernight") }]} />
        <Select label={t("plans.report.paymentLabel")} onValueChange={(value) => set("payment", value as PlanReportFilters["payment"])} value={filters.payment} options={[{ value: "all", label: all }, { value: "paid", label: t("plans.report.paymentPaid") }, { value: "receivable", label: t("plans.report.paymentReceivable") }, { value: "overdue", label: t("plans.report.paymentOverdue") }, { value: "none", label: t("plans.report.paymentNone") }]} />
        <Select label={t("plans.report.consumptionLabel")} onValueChange={(value) => set("consumption", value as PlanReportFilters["consumption"])} value={filters.consumption} options={[{ value: "all", label: all }, { value: "zero", label: t("plans.report.consumptionZero") }, { value: "low", label: t("plans.report.consumptionLow") }, { value: "available", label: t("plans.report.consumptionAvailable") }, { value: "unmetered", label: t("plans.report.consumptionUnmetered") }]} />
        <Select label={t("plans.report.statusLabel")} onValueChange={(value) => set("status", value as PlanReportFilters["status"])} value={filters.status} options={[{ value: "all", label: all }, { value: "active", label: t("plans.report.statusActive") }, { value: "ended", label: t("plans.report.statusEnded") }]} />
      </div>

      {hasRefinements ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{t("plans.report.filtersActive")}</span>
          <Button onClick={() => onChange({ ...DEFAULT_PLAN_REPORT_FILTERS, view: filters.view })} size="sm" variant="ghost">{t("plans.report.clearFilters")}</Button>
        </div>
      ) : null}
    </section>
  );
}
